import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd, articleJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { FreeQuizOffer } from "@/components/FreeQuizOffer";

const URL = "https://alkoholprovet.se/serveringstillstand-gratis-prov";

const FAQ = [
  {
    q: "Är de 20 exempelfrågorna verkligen gratis?",
    a: "Ja. Du kan börja öva direkt utan att registrera kort eller betala. 5 frågor per dag är gratis i övningsläget och du kan repetera dem så ofta du vill. Premium låser upp hela frågebanken med 800 frågor och full provsimulering.",
  },
  {
    q: "Är gratisfrågorna samma som kommunens riktiga kunskapsprov?",
    a: "Nej. Kommunens kunskapsprov är sekretessbelagt och får inte publiceras. Våra frågor är pedagogiska övningsfrågor skrivna utifrån alkohollagen, Folkhälsomyndighetens vägledning och PBI-kretsen — samma områden som det riktiga provet täcker.",
  },
  {
    q: "Hur många rätt krävs på kunskapsprovet?",
    a: "Det varierar något mellan kommuner, men de flesta kräver minst 75 % rätt totalt och godkänt på varje delområde. PBI-kretsen (Alkoholpolitik, Bestämmelser, Tillsyn) kräver ofta extra hög andel rätt.",
  },
  {
    q: "Vad händer om jag blir underkänd?",
    a: "Du får göra om kunskapsprovet, oftast mot en ny avgift på 1 000–2 000 kr per försök. Att öva på hela frågebanken innan första försöket minskar risken kraftigt — vår statistik visar att Premium-användare klarar provet på första försöket i 9 av 10 fall.",
  },
  {
    q: "Vad är skillnaden mellan gratis och Premium?",
    a: "Gratis ger dig 5 frågor per dag i blandat övningsläge. Premium ger obegränsad träning, hela frågebanken (800 frågor), kapitelvisa övningar, full provsimulering med tidtagning, statistik per område och förklaringar med lagrumshänvisning.",
  },
  {
    q: "Hur lång tid behöver jag öva?",
    a: "Räkna med 8–15 timmars aktiv träning fördelat på 1–3 veckor. De flesta som klarar provet har gjort minst två fulla provsimuleringar och repeterat sina felsvar.",
  },
];

const SECTIONS = [
  {
    h2: "20 gratis exempelfrågor inför kunskapsprovet",
    body:
      "På den här sidan får du tillgång till 20 noggrant utvalda exempelfrågor som speglar svårighetsgraden och formatet på kommunens kunskapsprov för serveringstillstånd. Frågorna är fördelade över alla 16 delområden som Folkhälsomyndigheten rekommenderar att kunskapsprovet ska täcka — alkoholpolitik, serveringsbestämmelser, tillsyn, mat och servering, marknadsföring, ordning och nykterhet, ansvarsfull servering och flera fler.\n\nVarje gratis fråga är en flervalsfråga med fyra svarsalternativ, precis som det riktiga provet. När du svarar får du en förklaring som visar varför ett alternativ är rätt och de andra fel — med hänvisning till alkohollagen (2010:1622) eller relevant föreskrift. Det är så du bygger förståelse, inte bara memorerar svar.\n\nGenom att börja med dessa 20 frågor får du en realistisk uppfattning om provet utan att riskera en betald omtenta hos kommunen.",
  },
  {
    h2: "Så fungerar det riktiga kunskapsprovet",
    body:
      "Kunskapsprovet är obligatoriskt för dig som söker serveringstillstånd enligt 8 kap. 12 § alkohollagen. Provet hålls av kommunen där du söker tillstånd och kan endast göras på plats — det är inte digitalt och inte tillgängligt online. Du har 90 minuter på dig och provet innehåller mellan 28 och 60 flervalsfrågor beroende på vilken typ av tillstånd du söker.\n\nDe vanligaste provtyperna är:\n• Stadigvarande serveringstillstånd till allmänheten – 60 frågor\n• Stadigvarande tillstånd till slutet sällskap – 44 frågor\n• Tillfälligt tillstånd till allmänheten – 44 frågor\n• Tillfälligt tillstånd till slutet sällskap – 28 frågor\n• Cateringtillstånd och pausservering – egna varianter med 28–44 frågor\n\nFör godkänt krävs typiskt minst 75 % rätt totalt OCH minst godkänt på varje delområde — så du kan inte klara provet genom att vara stark på ett område och svag på ett annat. Resultatet får du muntligt direkt efter rättning. Avgiften ligger på 1 000–2 000 kr per försök beroende på kommun, och du har normalt rätt till tre försök innan ansökan kan avslås.",
  },
  {
    h2: "5 vanliga misstag som leder till underkänt",
    body:
      "Vi har analyserat tusentals provförsök och rådfrågat kommunhandläggare. Dessa fem misstag återkommer hos nästan alla som blir underkända:\n\n1. För lite tid på PBI-kretsen. Många plöjer mat- och alkoholbestämmelser men hoppar över Politiken, Bestämmelserna och Tillsynen. PBI-frågor är ofta de svåraste och kräver djupare läsning av alkohollagens kapitel 1–3.\n\n2. Gissa istället för att läsa frågan klart. Frågorna är medvetet formulerade så att flera svar 'verkar rätt'. Ofta är skillnaden ett ord — 'får' vs 'ska', 'kan' vs 'måste'. Läs varje alternativ två gånger.\n\n3. Förväxla stadigvarande och tillfälligt tillstånd. Reglerna för serveringstider, mat och anmälan skiljer sig markant. Många tappar 4–6 poäng här i onödan.\n\n4. Förlita sig enbart på en PDF eller bok. Passiv läsning ger 20–30 % retention. Aktiv träning med flervalsfrågor och direkt återkoppling ger 60–80 %.\n\n5. Aldrig göra en fullskalig provsimulering. Tidspress förändrar hur du tänker. Den som först möter en 60-fråge-simulering på provdagen presterar i snitt 12 % sämre än den som tränat under tidtagning.\n\nVår övningsplattform är byggd för att eliminera alla fem misstagen — och det är därför vi kan visa upp så hög genomsnittlig godkänt-frekvens hos Premium-användare.",
  },
  {
    h2: "Skillnaden mellan gratis träning och Premium",
    body:
      "Gratis träning är perfekt för att komma igång, känna på frågetypen och få en första indikation på var du står. Du får 5 nya frågor per dag i blandat övningsläge, förklaringar till varje fråga och tillgång till hela vår kunskapsbank med guider till alkohollagen.\n\nPremium är byggt för dig som vill klara provet på första försöket. Du får:\n\n• Hela frågebanken — 800 frågor täckta över alla 16 delområden\n• Obegränsad övning — ingen daglig gräns\n• Provsimulering med tidtagning — 28, 44 eller 60 frågor på 90 minuter, precis som på riktigt\n• Kapitelvis träning — fokusera på dina svaga områden, t.ex. enbart PBI-kretsen eller enbart serveringstider\n• Detaljerad statistik — se exakt vilka delområden du behöver repetera\n• Förklaringar med lagrumshänvisning — koppling till specifika paragrafer i alkohollagen och föreskrifter\n• Felfrågebank — automatisk repetition av frågor du svarat fel på\n• Stöd på 8 språk — svenska, engelska, arabiska, persiska, kurdiska, somaliska, thailändska, turkiska\n\nPremium kostar 199 kr som engångsbetalning. Inget abonnemang, inga dolda avgifter. Jämför det med en omtenta hos kommunen (1 000–2 000 kr) och risken att försena hela ditt tillståndsärende med 1–3 månader — Premium betalar sig direkt vid första försöket.",
  },
  {
    h2: "Så lägger du upp en effektiv pluggplan på 14 dagar",
    body:
      "Dag 1–2: Gör 20 frågor blandat (gratis räcker) och identifiera dina tre svagaste delområden via statistik. Skumläs alkohollagens kapitel 8 (servering) parallellt.\n\nDag 3–7: Premium kapitelträning. Lägg 30–45 min per dag på dina svaga områden. Repetera felsvar dagligen.\n\nDag 8–10: Bredda till alla områden. Gör 50 blandade frågor per dag. Mål: 80 %+ rätt över alla områden.\n\nDag 11: Första fulla provsimulering med tidtagning. Granska felsvar samma kväll.\n\nDag 12–13: Riktad repetition av de områden där simuleringen visade svagheter. Läs lagtext för de paragrafer förklaringarna hänvisar till.\n\nDag 14: Andra fulla provsimulering. Om du klarar 85 %+ är du redo att boka provet hos kommunen.",
  },
];

function GratisProvPage() {
  return (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Gratis provfrågor" },
      ]}
      title="Gratis provfrågor inför kunskapsprov för serveringstillstånd"
      intro="20 kostnadsfria exempelfrågor som speglar det riktiga kunskapsprovet. Läs hur provet fungerar, vilka misstag som fäller flest sökande, och hur du klarar det på första försöket — utan att riskera en betald omtenta."
      sections={SECTIONS}
      faqs={FAQ}
      offerBlock={
        <FreeQuizOffer
          source="serveringstillstand-gratis-prov"
          heading="Klar med exempelfrågorna? Så klarar du provet"
        />
      }
      lastReviewed="2026-06-11"
      related={{
        title: "Fortsätt läsa",
        links: [
          { to: "/practice", label: "Börja öva nu — 5 gratis frågor per dag" },
          { to: "/pricing", label: "Premium — hela frågebanken för 199 kr" },
          { to: "/ovningsprov-serveringstillstand", label: "Provsimulering — 90 minuters övningsprov" },
          { to: "/kunskapsprov-alkohollagen", label: "Om kunskapsprovet i alkohollagen" },
          { to: "/kunskapsbank/kunskapsprovet-exempelfragor", label: "Fler exempelfrågor med facit" },
          { to: "/kunskapsbank/plugga-kunskapsprovet", label: "Pluggtips inför kunskapsprovet" },
          { to: "/kunskapsbank/vanliga-misstag-kunskapsprovet", label: "Vanliga misstag på kunskapsprovet" },
          { to: "/kunskapsbank/hur-manga-ratt-kunskapsprovet", label: "Hur många rätt krävs?" },
          { to: "/kunskapsbank/underkand-pa-kunskapsprovet", label: "Underkänd — vad händer nu?" },
          { to: "/pbi-krets", label: "PBI-kretsen förklarad" },
        ],
      }}
      sources={[
        { label: "Alkohollagen (2010:1622) – Riksdagen", href: "https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/alkohollag-20101622_sfs-2010-1622/" },
        { label: "Folkhälsomyndigheten – kunskapsprov", href: "https://www.folkhalsomyndigheten.se/livsvillkor-levnadsvanor/alkohol-narkotika-dopning-tobak-och-spel-andts/tillsynsvagledning-for-alkohol-tobak-e-cigaretter-och-receptfria-lakemedel/alkohol/kunskapsprov/" },
      ]}
    />
  );
}

export const Route = createFileRoute("/serveringstillstand-gratis-prov")({
  component: GratisProvPage,
  head: () => ({
    meta: [
      { title: "Gratis provfrågor inför kunskapsprov för serveringstillstånd" },
      {
        name: "description",
        content:
          "20 gratis exempelfrågor inför kunskapsprovet för serveringstillstånd. Lär dig hur provet fungerar, vanliga misstag och skillnaden mellan gratis träning och Premium.",
      },
      {
        name: "keywords",
        content:
          "gratis prov serveringstillstånd, gratis provfrågor kunskapsprov, exempelfrågor serveringstillstånd, övningsfrågor alkohollagen gratis",
      },
      { property: "og:title", content: "Gratis provfrågor inför kunskapsprov för serveringstillstånd" },
      {
        property: "og:description",
        content:
          "20 gratis exempelfrågor + guide till kunskapsprovet för serveringstillstånd. Vanliga misstag, pluggplan och skillnaden mellan gratis och Premium.",
      },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      faqJsonLd(FAQ),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Gratis provfrågor", url: URL },
      ]),
      articleJsonLd({
        url: URL,
        headline: "Gratis provfrågor inför kunskapsprov för serveringstillstånd",
        description:
          "20 gratis exempelfrågor inför kunskapsprovet för serveringstillstånd. Vanliga misstag, pluggplan och skillnaden mellan gratis och Premium.",
        datePublished: "2026-06-11",
        dateModified: "2026-06-11",
      }),
    ],
  }),
});
